import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  User,
  Home,
  PoundSterling,
  Loader2,
  CheckCircle,
  Plus,
  ArrowLeft,
  Building2,
  Mail,
  Phone,
} from "lucide-react";
import { API } from "@/App";
import PostcodeLookup from "@/components/PostcodeLookup";

export default function SimpleNewInstructionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  const searchRef = useRef(null);
  const preselectedClientId = searchParams.get("client");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Selected client
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);

  // Instruction type
  const [instructionType, setInstructionType] = useState("sales"); // sales or lettings

  // Property details
  const [property, setProperty] = useState({
    address_line1: "",
    address_line2: "",
    town: "",
    county: "",
    postcode: "",
    property_type: "semi_detached",
    bedrooms: 3,
    bathrooms: 1,
    receptions: 1,
  });

  // Pricing (sales)
  const [salesPricing, setSalesPricing] = useState({
    asking_price: "",
    fee_percentage: 1.25,
    agency_type: "sole",
  });

  // Pricing (lettings)
  const [lettingsPricing, setLettingsPricing] = useState({
    monthly_rent: "",
    management_type: "fully_managed",
    management_fee_percent: 10,
  });

  const [saving, setSaving] = useState(false);

  // Load pre-selected client from URL parameter
  useEffect(() => {
    const loadPreselectedClient = async () => {
      if (!preselectedClientId) return;
      
      setLoadingClient(true);
      try {
        const res = await axios.get(
          `${API}/contacts/${preselectedClientId}/full`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.client) {
          const client = res.data.client;
          setSelectedClient({
            id: client.contact_id || preselectedClientId,
            type: res.data.source_type || "contact",
            name: client.full_name || client.name || "",
            email: client.email || "",
            phone: client.mobile || client.phone || "",
            roles: client.roles || []
          });
          setSearchQuery(client.full_name || client.name || "");
        }
      } catch (error) {
        console.error("Failed to load pre-selected client:", error);
        toast.error("Could not load pre-selected client");
      } finally {
        setLoadingClient(false);
      }
    };
    
    loadPreselectedClient();
  }, [preselectedClientId, token]);

  // Search for clients
  useEffect(() => {
    const searchClients = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const res = await axios.get(
          `${API}/contacts/search/unified?q=${encodeURIComponent(searchQuery)}&limit=10`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSearchResults(res.data.results || []);
        setShowResults(true);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchClients, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, token]);

  // Handle client selection
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearchQuery(client.name);
    setShowResults(false);
  };

  // Handle create new client
  const handleCreateNewClient = () => {
    navigate(`/contacts/new?returnTo=/new-instruction&name=${encodeURIComponent(searchQuery)}`);
  };

  // Handle postcode lookup result
  const handleAddressSelect = (address) => {
    setProperty((prev) => ({
      ...prev,
      address_line1: address.line_1 || "",
      address_line2: address.line_2 || "",
      town: address.town_or_city || "",
      county: address.county || "",
      postcode: address.postcode || prev.postcode,
    }));
  };

  // Submit instruction
  const handleSubmit = async () => {
    if (!selectedClient) {
      toast.error("Please select a client");
      return;
    }
    if (!property.address_line1 || !property.postcode) {
      toast.error("Please enter property address");
      return;
    }

    setSaving(true);
    try {
      const fullAddress = [
        property.address_line1,
        property.address_line2,
        property.town,
        property.postcode,
      ]
        .filter(Boolean)
        .join(", ");

      if (instructionType === "sales") {
        if (!salesPricing.asking_price) {
          toast.error("Please enter asking price");
          setSaving(false);
          return;
        }

        const payload = {
          // Client
          seller_contact_id: selectedClient.id,
          seller_name: selectedClient.name,
          seller_email: selectedClient.email,
          seller_phone: selectedClient.phone,
          // Property
          property_address: fullAddress,
          address_line1: property.address_line1,
          address_line2: property.address_line2,
          town: property.town,
          county: property.county,
          postcode: property.postcode,
          property_type: property.property_type,
          bedrooms: property.bedrooms,
          bathrooms: property.bathrooms,
          reception_rooms: property.receptions,
          // Pricing
          asking_price: parseFloat(salesPricing.asking_price),
          fee_percentage: salesPricing.fee_percentage,
          agency_type: salesPricing.agency_type,
          // Status
          status: "active",
          current_stage: "instruction_taken",
        };

        const res = await axios.post(`${API}/sales/instructions`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        toast.success("Sales instruction created!");
        // Navigate to client dashboard to see full client view
        navigate(`/client/${selectedClient.id}`);
      } else {
        // Lettings
        if (!lettingsPricing.monthly_rent) {
          toast.error("Please enter monthly rent");
          setSaving(false);
          return;
        }

        const payload = {
          applicant_id: selectedClient.id,
          address_line1: property.address_line1,
          address_line2: property.address_line2,
          town: property.town,
          county: property.county,
          postcode: property.postcode,
          property_type: property.property_type,
          bedrooms: property.bedrooms,
          bathrooms: property.bathrooms,
          receptions: property.receptions,
          monthly_rent: parseFloat(lettingsPricing.monthly_rent),
          management_type: lettingsPricing.management_type,
          management_fee_percent: lettingsPricing.management_fee_percent,
          instruction_date: new Date().toISOString().split("T")[0],
        };

        const res = await axios.post(`${API}/instructions/lettings`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        toast.success("Lettings instruction created!");
        // Navigate to client dashboard to see full client view
        navigate(`/client/${selectedClient.id}`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Failed to create instruction");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">New Instruction</h1>
            <p className="text-slate-500">Add a property instruction for a client</p>
          </div>
        </div>

        {/* Instruction Type Toggle */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <Button
                variant={instructionType === "sales" ? "default" : "outline"}
                className={instructionType === "sales" ? "bg-purple-600 hover:bg-purple-700" : ""}
                onClick={() => setInstructionType("sales")}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Sales
              </Button>
              <Button
                variant={instructionType === "lettings" ? "default" : "outline"}
                className={instructionType === "lettings" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                onClick={() => setInstructionType("lettings")}
              >
                <Home className="w-4 h-4 mr-2" />
                Lettings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Client Search */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              1. Select Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  ref={searchRef}
                  placeholder="Type client name to search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedClient(null);
                  }}
                  onFocus={() => searchResults.length > 0 && setShowResults(true)}
                  className="pl-10"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                )}
              </div>

              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                  {searchResults.map((client) => (
                    <button
                      key={`${client.type}-${client.id}`}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between border-b last:border-0"
                      onClick={() => handleSelectClient(client)}
                    >
                      <div>
                        <div className="font-medium text-slate-900">{client.name}</div>
                        <div className="text-sm text-slate-500 flex items-center gap-3">
                          {client.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {client.email}
                            </span>
                          )}
                          {client.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {client.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {client.roles?.map((role) => (
                          <Badge key={role} variant="secondary" className="text-xs">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* No results - offer to create */}
              {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-4">
                  <p className="text-slate-500 mb-3">No clients found for "{searchQuery}"</p>
                  <Button onClick={handleCreateNewClient} variant="outline" className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Client
                  </Button>
                </div>
              )}
            </div>

            {/* Selected Client Display */}
            {selectedClient && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Client Selected</span>
                </div>
                <div className="text-slate-700">
                  <div className="font-semibold">{selectedClient.name}</div>
                  <div className="text-sm text-slate-500">
                    {selectedClient.email} {selectedClient.phone && `• ${selectedClient.phone}`}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Property Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5" />
              2. Property Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Postcode Lookup */}
            <div>
              <Label>Postcode Lookup</Label>
              <PostcodeLookup
                onSelect={handleAddressSelect}
                onPostcodeChange={(pc) => setProperty((prev) => ({ ...prev, postcode: pc }))}
              />
            </div>

            {/* Address Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Address Line 1</Label>
                <Input
                  value={property.address_line1}
                  onChange={(e) => setProperty((prev) => ({ ...prev, address_line1: e.target.value }))}
                  placeholder="e.g. 123 High Street"
                />
              </div>
              <div className="col-span-2">
                <Label>Address Line 2</Label>
                <Input
                  value={property.address_line2}
                  onChange={(e) => setProperty((prev) => ({ ...prev, address_line2: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Town/City</Label>
                <Input
                  value={property.town}
                  onChange={(e) => setProperty((prev) => ({ ...prev, town: e.target.value }))}
                />
              </div>
              <div>
                <Label>Postcode</Label>
                <Input
                  value={property.postcode}
                  onChange={(e) => setProperty((prev) => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
                />
              </div>
            </div>

            {/* Property Type */}
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2">
                <Label>Property Type</Label>
                <Select
                  value={property.property_type}
                  onValueChange={(v) => setProperty((prev) => ({ ...prev, property_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="detached">Detached</SelectItem>
                    <SelectItem value="semi_detached">Semi-Detached</SelectItem>
                    <SelectItem value="terraced">Terraced</SelectItem>
                    <SelectItem value="flat">Flat/Apartment</SelectItem>
                    <SelectItem value="bungalow">Bungalow</SelectItem>
                    <SelectItem value="maisonette">Maisonette</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bedrooms</Label>
                <Input
                  type="number"
                  min="0"
                  value={property.bedrooms}
                  onChange={(e) => setProperty((prev) => ({ ...prev, bedrooms: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Bathrooms</Label>
                <Input
                  type="number"
                  min="0"
                  value={property.bathrooms}
                  onChange={(e) => setProperty((prev) => ({ ...prev, bathrooms: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Pricing */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PoundSterling className="w-5 h-5" />
              3. {instructionType === "sales" ? "Sale Price & Fees" : "Rent & Management"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {instructionType === "sales" ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Asking Price (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={salesPricing.asking_price}
                      onChange={(e) =>
                        setSalesPricing((prev) => ({ ...prev, asking_price: e.target.value }))
                      }
                      placeholder="e.g. 350000"
                    />
                  </div>
                  <div>
                    <Label>Fee (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={salesPricing.fee_percentage}
                      onChange={(e) =>
                        setSalesPricing((prev) => ({
                          ...prev,
                          fee_percentage: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label>Agency Type</Label>
                  <Select
                    value={salesPricing.agency_type}
                    onValueChange={(v) => setSalesPricing((prev) => ({ ...prev, agency_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole">Sole Agency</SelectItem>
                      <SelectItem value="multi">Multi Agency</SelectItem>
                      <SelectItem value="joint_sole">Joint Sole Agency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monthly Rent (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={lettingsPricing.monthly_rent}
                      onChange={(e) =>
                        setLettingsPricing((prev) => ({ ...prev, monthly_rent: e.target.value }))
                      }
                      placeholder="e.g. 1200"
                    />
                  </div>
                  <div>
                    <Label>Management Fee (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={lettingsPricing.management_fee_percent}
                      onChange={(e) =>
                        setLettingsPricing((prev) => ({
                          ...prev,
                          management_fee_percent: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label>Management Type</Label>
                  <Select
                    value={lettingsPricing.management_type}
                    onValueChange={(v) =>
                      setLettingsPricing((prev) => ({ ...prev, management_type: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fully_managed">Fully Managed</SelectItem>
                      <SelectItem value="tenant_find">Tenant Find Only</SelectItem>
                      <SelectItem value="rent_collection">Rent Collection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !selectedClient}
            className={instructionType === "sales" ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Create Instruction
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
